import { randomInt, type RngState } from './rng';

/** A shuffled copy of the input and the generator state to continue from. */
export interface ShuffleResult<T> {
  readonly items: T[];
  readonly rng: RngState;
}

/**
 * Deterministic Fisher-Yates shuffle. Returns a new array and the next
 * generator state; the input is never modified. The same items and state always
 * give the same ordering, and feeding the returned state into the next shuffle
 * continues one reproducible sequence.
 */
export function shuffle<T>(
  items: readonly T[],
  state: RngState,
): ShuffleResult<T> {
  const result = [...items];
  let rng = state;
  for (let i = result.length - 1; i > 0; i--) {
    const [j, next] = randomInt(rng, i + 1);
    rng = next;
    const held = result[i] as T;
    result[i] = result[j] as T;
    result[j] = held;
  }
  return { items: result, rng };
}
